import React, { useEffect } from 'react';
import { useParams, useHistory } from 'react-router';
import { useDispatch } from 'react-redux';

// Constants
import Colors from 'Wpds/colors/ColorConstants';

// Helpers
import { toggleIntercom } from 'Shared/helpers';

// Types
import { GoalItem } from 'Shared/constants/types';

// Hooks
import { useResponsiveScreenWidth } from 'Wpds/hooks/useResponsiveScreenWidth';

// Utils
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';
import { isEmpty } from 'Wpds/components/rich-text/utils/EditorState.utils';

// Actions
import { updateParentGoal } from 'Shared/actions/Goals.actions';

// Hooks
import { useGoal } from 'Shared/utils/hooks/use-goal';
import { useToggle } from 'Shared/utils/hooks';

// Components
import { WpPageLayout, WpPageHeader, WpPageBody } from 'Wpds/components/page-layout';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import { WpContainer, WpContainerBody, WpContainerHeader, WpContainerFooter } from 'Wpds/components/container';
import { WpIcon } from 'Wpds/components/icon';
import { WpDetailList, WpDetailListItem } from 'Wpds/components/detail-list';
import WpDivider from 'Wpds/components/divider/WpDivider';
import { WpDrawerV2, WpDrawerV2Body } from 'Wpds/components/drawer';
import WpLabelled from 'Wpds/components/labelled/WpLabelled';
import GoalName from 'Shared/components/Goals/GoalName/GoalName';
import GoalStatus from 'Shared/components/Goals/GoalStatus/GoalStatus';
import GoalDueDate from 'Shared/components/Goals/GoalDueDate/GoalDueDate';
import GoalOwner from 'Shared/components/Goals/GoalOwner/GoalOwner';
import GoalWorkspace from 'Shared/components/Goals/GoalWorkspace/GoalWorkspace';
import GoalDepartment from 'Shared/components/Goals/GoalDepartment/GoalDepartment';
import GoalCompleteAlert from 'Shared/components/Goals/GoalCompleteAlert/GoalCompleteAlert';
import GoalHistory from 'Shared/components/Goals/GoalHistory/GoalHistory';
import GoalCompleteButton from 'Shared/components/Goals/GoalCompleteButton/GoalCompleteButton';
import GoalActionsDropdown from 'Shared/components/Goals/GoalActionsDropdown/GoalActionsDropdown';
import GoalDoesNotExist from 'Shared/components/Goals/GoalDoesNotExist/GoalDoesNotExist';
import GoalsTable, { GoalTableColumns } from 'Shared/components/Goals/GoalsTable/GoalsTable';
import GoalComments from 'Shared/components/Goals/GoalComments/GoalComments';
import GoalCommentInput from 'Shared/components/Goals/GoalCommentInput/GoalCommentInput';
import GoalActionItems from 'Shared/components/Goals/GoalActionItems/GoalActionItems';
import GoalProgress from 'Shared/components/Goals/GoalProgress/GoalProgress';
import AddSubGoal from 'Shared/components/Goals/AddSubGoal/AddSubGoal';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';
import GoalCustomTableRow from 'Shared/components/Goals/GoalCustomTableRow/GoalCustomTableRow';
import GoalDescription from 'Shared/components/Goals/GoalDescription/GoalDescription';
import GoalCheckinDate from 'Shared/components/Goals/GoalCheckinDate/GoalCheckinDate';

// Styles
import './GoalDetailsPage.scss';

type Props = {
  handleCloseClick: () => void;
};

const GoalDetailsPage = ({ handleCloseClick }: Props) => {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const { isLarge } = useResponsiveScreenWidth({ isAtLeast: true });

  const {
    goal,
    isFetchingGoal,
    hasError,
    isComplete,
    parentGoal,
    subGoals,
    canChangeStatus,
    isProgressDollarAmount,
    isProgressPercentage,
    isProgressNumber,
    isProgressActionItems,
    isProgressSubGoals,
  } = useGoal({ slug, fetchOnMount: true });

  const [isHistoryDrawerOpen, toggleIsHistoryDrawerOpen] = useToggle(false);

  const handleUnlinkSubGoal = slug => {
    dispatch(updateParentGoal({ goal: { slug, parentId: null } }));
  };

  const handleUnlinkParentGoal = e => {
    e.stopPropagation();
    dispatch(updateParentGoal({ goal: { slug: goal.slug, parentId: null } }));
  };

  const CustomSubLinkActions = ({ goal }: { goal: GoalItem }) => {
    const [isRemoving, toggleIsRemoving] = useToggle(false);
    return (
      <WpButton
        onClick={e => {
          e.stopPropagation();
          toggleIsRemoving();
          handleUnlinkSubGoal(goal.slug);
        }}
        size="xs"
        square
        variant="tertiary"
        loading={isRemoving}
        qaTest={`unlink-children-goal-${goal.slug}`}
      >
        <WpIcon name="unlink" />
      </WpButton>
    );
  };

  const handleRelatedGoalClick = (goal: GoalItem) => {
    history.push(PageUrlsUtils.makeGoalDetailPageUrl(goal.slug));
  };

  useEffect(() => {
    toggleIntercom({ show: false });
    return () => {
      toggleIntercom({ show: true });
    };
  }, []);

  const baseClassName = 'GoalDetailsPageV3';

  if (!goal && !isFetchingGoal && !hasError) return null;

  if (!goal && !isFetchingGoal && hasError)
    return (
      <WpPageLayout width="container">
        <WpPageBody>
          <GoalDoesNotExist />
        </WpPageBody>
      </WpPageLayout>
    );

  if (!goal && isFetchingGoal)
    return (
      <WpPageLayout width="container">
        <WpPageBody>loading...</WpPageBody>
      </WpPageLayout>
    );

  const details = (
    <WpContainer>
      <WpContainerHeader>Details</WpContainerHeader>
      <WpContainerBody>
        <WpDetailList>
          <WpDetailListItem label="Status">
            <GoalStatus goal={goal} editable />
          </WpDetailListItem>

          <WpDetailListItem label="Due">
            <GoalDueDate goal={goal} editable />
          </WpDetailListItem>
          <WpDetailListItem label="Owner">
            <GoalOwner goal={goal} editable />
          </WpDetailListItem>
        </WpDetailList>
        <WpDivider padding={20} />
        <WpDetailList>
          {goal.nextCheckin && (
            <WpDetailListItem label="Next Check-in">
              <GoalCheckinDate goal={goal} />
            </WpDetailListItem>
          )}
          <WpDetailListItem label="Workspace">
            <GoalWorkspace goal={goal} />
          </WpDetailListItem>
          <WpDetailListItem label="Department">
            <GoalDepartment goal={goal} />
          </WpDetailListItem>
        </WpDetailList>
      </WpContainerBody>
    </WpContainer>
  );

  const parentDetails = (
    <WpLabelled label="Parent Goal" id="parent-goal-details" showRequiredIndicator={false}>
      <WpContainer className={`${baseClassName}__parent-goal`} onClick={() => handleRelatedGoalClick(parentGoal)}>
        <WpContainerBody>
          <GoalName goal={parentGoal} />
          <div className={`${baseClassName}__parent-goal__progress`}>
            <GoalProgress goal={parentGoal} showEmptyState={false} />
          </div>
        </WpContainerBody>
        <WpContainerFooter>
          <WpButton variant="tertiary" onClick={handleUnlinkParentGoal} size="xs" square slimPadding>
            Unlink
          </WpButton>
        </WpContainerFooter>
      </WpContainer>
    </WpLabelled>
  );

  return (
    <>
      <WpPageLayout width="container">
        <WpPageHeader slim sticky width="full">
          <div className={`${baseClassName}__header`}>
            <div>{/* <GoalName goal={goal} /> */}</div>
            <WpButtonGroup>
              <GoalActionsDropdown goal={goal} />
              <WpButton size="sm" variant="tertiary" onClick={toggleIsHistoryDrawerOpen} square>
                <WpIcon name="history" />
              </WpButton>
              <WpButton
                qaTest="btn-goal-detail-page-done"
                size="sm"
                variant="tertiary"
                onClick={handleCloseClick}
                square
              >
                <WpIcon name="times" />
              </WpButton>
            </WpButtonGroup>
          </div>
        </WpPageHeader>
        <WpPageBody>
          <div className={`${baseClassName}__body`}>
            <div className={`${baseClassName}__body__main ${isLarge ? `${baseClassName}__body__main--lg` : ''}`}>
              <div className={`${baseClassName}__body__main__content`}>
                <div className={`${baseClassName}__name`}>
                  <GoalName goal={goal} size="lg" truncateName={false} showLinkedIndicator={false} editable />
                </div>
                {!isLarge && <GoalCompleteButton goal={goal} />}
                {isComplete && (
                  <div className={`${baseClassName}__complete-alert`}>
                    <GoalCompleteAlert goal={goal} />
                  </div>
                )}
                {isEmpty(goal.description) && !canChangeStatus ? null : (
                  <div>
                    <WpLabelled label="Description" id="goal-descrition-labbeled" showRequiredIndicator={false}>
                      <GoalDescription goal={goal} editable />
                    </WpLabelled>
                  </div>
                )}
                {!isLarge && details}
                {(isProgressDollarAmount || isProgressPercentage || isProgressNumber) && (
                  <div>
                    <GoalProgress goal={goal} inContainer view="page" />
                  </div>
                )}
                {isProgressActionItems && (
                  <div>
                    <WpLabelled id="action-items-labelled" label="Action Items" showRequiredIndicator={false}>
                      <GoalActionItems goal={goal} />
                    </WpLabelled>
                  </div>
                )}
                <div>
                  <WpLabelled id="sub-goals-labelled" label="Sub-Goals" showRequiredIndicator={false}>
                    <WpContainer>
                      {isProgressSubGoals && subGoals.length > 0 && (
                        <WpContainerHeader>
                          <p className={`${baseClassName}__progress-header`}>Goal Progress</p>
                          <GoalProgress goal={goal} view="page" />
                        </WpContainerHeader>
                      )}
                      {subGoals.length === 0 && <EmptyStateContainer subTitle="There are no sub-goals here yet." />}
                      {subGoals.length > 0 && (
                        <GoalsTable
                          goals={subGoals}
                          slimRow
                          showTableHeader={false}
                          showInContainer={false}
                          showBorder={false}
                          onRowClick={handleRelatedGoalClick}
                          hiddenColumns={[
                            // GoalTableColumns.actions,
                            GoalTableColumns.department,
                            GoalTableColumns.owner,
                            GoalTableColumns.progress,
                            GoalTableColumns.status,
                            GoalTableColumns.due,
                            GoalTableColumns.workspace,
                          ]}
                          components={{
                            [GoalTableColumns.name]: ({ goal }) => <GoalCustomTableRow goal={goal} />,
                            [GoalTableColumns.actions]: CustomSubLinkActions,
                          }}
                        />
                      )}
                    </WpContainer>
                    <div className={`${baseClassName}__body__sub-goal-add`}>
                      <AddSubGoal goalId={goal.id} />
                    </div>
                  </WpLabelled>
                </div>
                {!isLarge && parentGoal && parentDetails}
                {!isProgressActionItems && (
                  <div>
                    <WpLabelled id="action-items-labelled" label="Action Items" showRequiredIndicator={false}>
                      <GoalActionItems goal={goal} />
                    </WpLabelled>
                  </div>
                )}
                <WpLabelled id="comments-labelled" label="Comments" showRequiredIndicator={false}>
                  <WpContainer className={`${baseClassName}__body__comments`}>
                    <WpContainerBody>
                      <GoalComments goal={goal} />
                    </WpContainerBody>
                    <WpContainerFooter backgroundColor={Colors.gray[50]}>
                      <div className={`${baseClassName}__body__comments-inputs`}>
                        <GoalCommentInput goal={goal} />
                      </div>
                    </WpContainerFooter>
                  </WpContainer>
                </WpLabelled>
              </div>
              {/* <div className={`${baseClassName}__body__comments-inputs`}>
                <GoalCommentInput goal={goal} />
              </div> */}
            </div>

            {isLarge && (
              <div className={`${baseClassName}__body__rail`}>
                <GoalCompleteButton goal={goal} />
                {details}
                {parentGoal && parentDetails}
              </div>
            )}
          </div>
        </WpPageBody>
      </WpPageLayout>
      <WpDrawerV2 isOpen={isHistoryDrawerOpen} toggleIsOpen={toggleIsHistoryDrawerOpen} title="History">
        <WpDrawerV2Body>
          <GoalHistory goal={goal} />
        </WpDrawerV2Body>
      </WpDrawerV2>
    </>
  );
};

export default GoalDetailsPage;
